"use client";

import React, { ReactNode } from "react";
import * as RadixTooltip from "@radix-ui/react-tooltip";

import styles from "./Tooltip.module.scss";

import { cn } from "@/utils/cn";

enum ContentSide {
  TOP = "top",
  RIGHT = "right",
  BOTTOM = "bottom",
  LEFT = "left",
}

enum ContentAlign {
  START = "start",
  CENTER = "center",
  END = "end",
}

interface Props {
  text: ReactNode;
  children: ReactNode | ReactNode[];
  onOpenChange?: (open: boolean) => void;
  open?: boolean;
  contentSide?: ContentSide;
  contentAlign?: ContentAlign;
  avoidCollisions?: boolean;
  tip?: boolean;
  delay?: number;
  invertColors?: boolean;
  centerText?: boolean;
  /** Small modern bubble: 12px text, rounded, wraps long text at 220px. */
  compact?: boolean;
}

const Tooltip: React.FC<Props> = ({
  text,
  children,
  onOpenChange,
  open,
  contentSide = ContentSide.BOTTOM,
  contentAlign = ContentAlign.CENTER,
  avoidCollisions = true,
  delay = 400,
  tip = true,
  invertColors = true,
  centerText = true,
  compact = false,
}) => (
  <RadixTooltip.Provider>
    <RadixTooltip.Root
      delayDuration={delay}
      {...(typeof open !== "undefined" && { open })}
      {...(onOpenChange && { onOpenChange })}
    >
      <RadixTooltip.Trigger aria-label="Open tooltip" asChild>
        <span className={styles.trigger}>{children}</span>
      </RadixTooltip.Trigger>
      <RadixTooltip.Content
        sideOffset={2}
        side={contentSide}
        align={contentAlign}
        avoidCollisions={avoidCollisions}
        className={cn(styles.content, {
          [styles.noInverse]: invertColors === false,
          [styles.noCenter]: centerText === false,
          [styles.compact]: compact,
        })}
      >
        {text}
        {tip && <RadixTooltip.Arrow />}
      </RadixTooltip.Content>
    </RadixTooltip.Root>
  </RadixTooltip.Provider>
);

export { Tooltip, ContentSide, ContentAlign };
